import { TextBadge } from '@carvana/ds-badge';
import Button, { NavigableButton } from '@carvana/ds-button';
import { ChatLogoCvna, TaxCreditCvna } from '@carvana/ds-icons';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { EventedModal, ModalNames } from '|/app/modals';
import { CancelOrderModal } from '|/app/navigation-rail/components/modals';
import Loading from '|/components/base/loading';
import NoDealerFeesModal from '|/components/carvana-ds/no-dealer-fees-modal';
import {
  OrderSummaryDisplay,
  ContactDetails,
  GettingVehicle,
  HowYourePaying,
  PriceBreakdown,
  TradeInModal,
  DealerMarketPlaceReminder
} from '|/components/carvana-ds/order-summary-display';
import CarvanaCareModal from '|/components/carvana-ds/order-summary-display/carvana-care-modal';
import { SupportMfeModal } from '|/components/support-contact';
import { getDealerFeeData, getDealerFeeAmount } from '|/helpers/dealer-fees';
import { useIsInBucket } from '|/helpers/experiment';
import { isCarvanaNativeApp, isAndroidNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { useIsMobileViewport } from '|/hooks/use-is-mobile-viewport';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './order-summary.module.css';
import { isNewVehicle } from '|/helpers';

interface OrderSummaryProps {
  summary: ReviewSummaryResponse;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isCheckoutPage: boolean;
  onEditOrder: () => Promise<void>;
}

const OrderSummary = ({
  summary,
  isOpen,
  setIsOpen,
  isCheckoutPage,
  onEditOrder
}: OrderSummaryProps) => {
  const {
    vehicle: {
      year = null,
      make = null,
      model = null,
      odometer = 0,
      trim = null,
      primaryImageUrl = null,
      fallbackImageUrl = null,
      isEvTaxCreditEligible = false,
      offering = null,
      id: vehicleId = null
    } = {},
    totals: { vehicleStickerPrice = null } = {}
  } = summary || {};
  const [isTradeInModalOpen, setIsTradeInModalOpen] = useState(false);
  const [isDealerFeesModalOpen, setIsDealerFeesModalOpen] = useState(false);
  const [isCarvanaCareModalOpen, setIsCarvanaCareModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { trackEvent } = useAnalyticsContext();
  const location = useLocation();
  const isMobileViewport = useIsMobileViewport();

  const isInNoDealerFeesExperiment = useIsInBucket('pp-no-dealer-fees', 'treatment', ['control']);

  const handleSupportClick = useCallback(() => {
    trackEvent('pp_header_support_contact_clicked');
    if (isCarvanaNativeApp()) {
      const eventName = isAndroidNativeWebview()
        ? MobileWebviewEvents.OPEN_SEBASTIAN
        : MobileWebviewEvents.OPEN_SUPPORT;
      setIsOpen(false);
      sendNativeEvent({
        event: eventName,
        payload: { currentStep: location.pathname },
        source: 'purchase'
      });
      return;
    }
    setIsOpen(false);
    setIsSupportModalOpen(true);
  }, [trackEvent, setIsOpen, location.pathname]);

  const formatMileage = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(Math.abs(value));
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsTradeInModalOpen(false);
    setIsDealerFeesModalOpen(false);
  }, [setIsOpen, setIsTradeInModalOpen]);

  if (isInNoDealerFeesExperiment === null) return <Loading />;

  let eventMetadata;
  const userState = summary?.customer?.address?.state;
  if (userState) {
    const stateData = getDealerFeeData(userState);
    if (stateData) {
      eventMetadata = {
        feeBasedState: stateData.stateCode,
        maxDealerFeeDisplayed: getDealerFeeAmount(stateData)
      };
    } else {
      eventMetadata = { feeBasedState: userState, maxDealerFeeDisplayed: null };
    }
  } else {
    eventMetadata = { feeBasedState: null, maxDealerFeeDisplayed: null };
  }

  const modalStyleProps = isMobileViewport
    ? ({ mobileStyle: 'full' } as const)
    : ({ drawerStyle: 'all', drawerSide: 'right', drawerWidth: '400px' } as const);

  return (
    <>
      <EventedModal
        analyticsId={ModalNames.OrderSummary}
        isOpen={isOpen}
        onClose={handleClose}
        modalHeader={
          isCheckoutPage ? (
            <div className={styles.modalHeaderContent}>
              <span className={styles.modalTitleDesktop}>Order Summary</span>
              <span className={styles.modalTitleSpacer} aria-hidden="true">
                {'\u00A0'}
              </span>
              <button
                className={styles.modalTitleMobile}
                onClick={handleSupportClick}
                type="button"
                data-analytics-id="pp_order_summary_support_contact"
              >
                <ChatLogoCvna size={16} color="var(--icon-primary)" />
                <span>Support &amp; Contact</span>
              </button>
            </div>
          ) : (
            'Order Summary'
          )
        }
        {...modalStyleProps}
        contentPadding={false}
        disableOutsideClose={
          isTradeInModalOpen || isDealerFeesModalOpen || isCarvanaCareModalOpen || isCancelModalOpen
        }
        divLine={false}
        eventMetadata={eventMetadata}
      >
        <div className={styles.vehicleCard}>
          <div className={styles.vehicleRow}>
            <div className={styles.vehicleImage}>
              <img
                src={primaryImageUrl ?? fallbackImageUrl ?? '/images/no-image.png'}
                alt="Vehicle"
              />
            </div>
            <div className={styles.vehicleContent}>
              <div className={styles.badges}>
                {isNewVehicle(summary?.vehicle) && (
                  <TextBadge customBgColor="var(--decorative-3-strong)" priority="custom">
                    Brand new
                  </TextBadge>
                )}
                {isEvTaxCreditEligible && (
                  <div className={styles.taxCreditBadge} data-testid="tax-credit-badge">
                    <TaxCreditCvna size={16} />
                    <span>Tax Credit</span>
                  </div>
                )}
              </div>
              <div className={styles.vehicleInfo}>
                <h2 className={styles.vehicleTitle}>
                  {[year, make, model].filter(Boolean).join(' ')}
                </h2>
                <p>{trim}</p>
                <p>
                  {vehicleStickerPrice != null && (
                    <>${vehicleStickerPrice.toLocaleString('en-US')} · </>
                  )}
                  {formatMileage(odometer)} miles
                </p>
              </div>
            </div>
          </div>
          {(vehicleId != null || isCheckoutPage) && (
            <div className={styles.actionLinks}>
              {vehicleId != null && (
                <NavigableButton
                  className={styles.actionButton}
                  href={`/vehicle/${vehicleId}`}
                  size="xs"
                  variant="secondary"
                  utility
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    if (isCarvanaNativeApp()) {
                      e.preventDefault();
                      sendNativeEvent({
                        event: 'continue_to_vdp',
                        payload: { vehicleId, source: 'checkout_order_summary' },
                        source: 'purchase'
                      });
                    }
                  }}
                  data-analytics-id="order-summary-view-vehicle"
                >
                  View vehicle
                </NavigableButton>
              )}
              {isCheckoutPage && (
                <Button
                  className={styles.actionButton}
                  size="xs"
                  variant="secondary"
                  utility
                  onClick={() => void onEditOrder()}
                  data-analytics-id="order-summary-edit-order"
                >
                  Edit order
                </Button>
              )}
            </div>
          )}
        </div>

        <OrderSummaryDisplay
          variant="sidebar"
          isInNoDealerFeesExperiment={isInNoDealerFeesExperiment}
        >
          <GettingVehicle summary={summary} variant="sidebar" vehicleType={offering ?? undefined} />

          <ContactDetails summary={summary} />

          <PriceBreakdown
            summary={summary}
            vinEtchingStatus={null}
            setIsTradeInModalOpen={setIsTradeInModalOpen}
            setIsCarvanaCareModalOpen={setIsCarvanaCareModalOpen}
            setIsDealerFeesModalOpen={setIsDealerFeesModalOpen}
            isInNoDealerFeesExperiment={isInNoDealerFeesExperiment}
          />

          <HowYourePaying summary={summary} />

          <DealerMarketPlaceReminder summary={summary} />

          <TradeInModal
            summary={summary}
            isOpen={isTradeInModalOpen}
            setIsOpen={setIsTradeInModalOpen}
          />

          <CarvanaCareModal
            isOpen={isCarvanaCareModalOpen}
            setIsOpen={setIsCarvanaCareModalOpen}
            coverageType={
              (summary?.product?.type as 'Essential' | 'Cosmetic' | 'Plus' | 'Premier') ||
              'Essential'
            }
            coverageDetails={{
              duration: `${summary?.product?.months} months`,
              mileage: `+${summary?.product?.mileage?.toLocaleString()} miles`
            }}
            fuelDescription={summary?.vehicle?.fuelDescription}
          />

          {isInNoDealerFeesExperiment && (
            <NoDealerFeesModal
              isOpen={isDealerFeesModalOpen}
              onClose={() => {
                setIsDealerFeesModalOpen(false);
              }}
              userState={userState}
            />
          )}

          {isCheckoutPage && (
            <>
              <button
                className={styles.cancelSection}
                onClick={() => {
                  setIsCancelModalOpen(true);
                }}
                type="button"
                data-analytics-id="pp_order_summary_cancel_link"
              >
                <span className={styles.cancelTextRegular}>
                  Want to <span className={styles.cancelTextLink}>cancel your order?</span>
                </span>
              </button>
              <CancelOrderModal
                isOpen={isCancelModalOpen}
                onClose={() => {
                  setIsCancelModalOpen(false);
                }}
              />
            </>
          )}
        </OrderSummaryDisplay>
      </EventedModal>
      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
      />
    </>
  );
};

export default OrderSummary;
